import { Component, type ReactNode, type ReactElement, type ErrorInfo } from 'react';
import { QueryError } from '@atlassian/jira-business-error-handling/src/utils/query-error/index.tsx';
import type { CaughtError } from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';

export type InvalidJQLErrorBoundaryProps = {
	children: ReactNode;
	render: (clearError: () => void) => ReactElement;
};

type State = {
	caughtError: CaughtError | null;
};

const isInvalidJQLError = (error: Error): boolean => {
	if (error instanceof QueryError) {
		if (
			(error.extensions || []).find(
				(err) => err.statusCode === 400 && err.errorType === 'FIELD_MISSING_HANDLER',
			)
		) {
			return true;
		}
	}

	return false;
};

export class InvalidJQLErrorBoundary extends Component<InvalidJQLErrorBoundaryProps, State> {
	state = { caughtError: null };

	componentDidCatch(error: Error, info: ErrorInfo) {
		if (isInvalidJQLError(error)) {
			this.setState({ caughtError: { error, info } });
			return;
		}
		throw error;
	}

	clearError = () => {
		this.setState({ caughtError: null });
	};

	render() {
		const { caughtError } = this.state;
		const { children, render: renderFallback } = this.props;

		if (!caughtError) {
			return children;
		}

		return renderFallback(this.clearError);
	}
}
